import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Printer } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, dmyTime } from "@/lib/format";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { PrintPreview } from "@/components/PrintDoc";
import { BirthRecordDoc, BirthPaymentReceiptDoc } from "@/components/VitalRecordDocs";
import {
  SEX_OPTS, RELIGION_OPTS, PLACE_OPTS, ATTENTION_OPTS, DELIVERY_OPTS,
  EMPTY_ADDRESS, EMPTY_REGISTRAR, Sec, Grid, Txt, Num, Sel,
  AddressFields, RegistrarFields,
} from "./RecordsShared";

const EMPTY = {
  date_of_birth: "", sex: "", child: { name: "", aadhaar_no: "" },
  father: { name: "", aadhaar_no: "", mobile_no: "", email_id: "" },
  mother: { name: "", aadhaar_no: "", mobile_no: "", email_id: "" },
  address_at_birth: { ...EMPTY_ADDRESS }, permanent_address: { ...EMPTY_ADDRESS },
  place_of_birth: { type: "hospital_institution", institution_name: "", address: { ...EMPTY_ADDRESS } },
  informant: { name: "", aadhaar_no: "", mobile_no: "", email_id: "", address: { ...EMPTY_ADDRESS } },
  declaration: false, declaration_date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }), informant_signature: "",
  mother_residence: { type: "", name: "", sub_district: "", state_ut: "", pin_code: "" },
  religion_father: "", religion_mother: "", father_education: "", mother_education: "",
  father_occupation: "", mother_occupation: "", mother_age_at_marriage: "", mother_age_at_birth: "",
  children_born_alive: "", attention_at_delivery: "", method_of_delivery: "",
  birth_weight_kg: "", duration_pregnancy_weeks: "", birth_order: "single",
  registrar: { ...EMPTY_REGISTRAR },
  birth_charge: "", birth_charge_note: "",
  birth_pay_mode: "cash", birth_pay_reference: "",
  birth_pay_split: false, birth_pay_rows: [{ amount: "", mode: "cash", reference_no: "" }],
};

// Modes the shared HMS billing ledger accepts for a birth-registration collection.
const PAY_MODES = ["cash", "upi", "card", "bank", "insurance", "cheque", "online", "other"];

const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

export default function BirthRecords() {
  const { can } = useAuth();
  const manage = can("hospital.birth.manage");
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [printRecord, setPrintRecord] = useState(null);
  // Birth billing lives in the shared hospital invoice ledger, reached through
  // /hospital/records/births/{id}/billing. `payFor` is the row being collected.
  const [payFor, setPayFor] = useState(null);
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState("");
  const [payForm, setPayForm] = useState({ amount: "", mode: "cash", reference_no: "" });
  // Split-payment rows: one row per payment mode (e.g. ₹400 cash + ₹600 UPI).
  const [payRows, setPayRows] = useState([{ amount: "", mode: "cash", reference_no: "" }]);
  const [splitMode, setSplitMode] = useState(false);
  const [printReceipt, setPrintReceipt] = useState(null);
  const [printBusy, setPrintBusy] = useState(false);
  const canPay = can("hospital.payments.manage");
  const canBill = can("hospital.billing.manage");

  const load = useCallback(async () => {
    setItems(null);
    try { const { data } = await api.get("/hospital/records/births", { params: { q: q || undefined } }); setItems(data.items); }
    catch (e) { setError(apiError(e)); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const set = (path) => (v) => setForm((f) => {
    const keys = path.split("."); const clone = { ...f }; let node = clone;
    for (let i = 0; i < keys.length - 1; i++) { node[keys[i]] = { ...(node[keys[i]] || {}) }; node = node[keys[i]]; }
    node[keys[keys.length - 1]] = v; return clone;
  });

  const openNew = () => { setEditing(null); setForm(EMPTY); setFormError(""); setOpen(true); };
  const openEdit = async (row) => {
    try {
      const { data } = await api.get(`/hospital/records/births/${row.birth_id}`);
      const r = data.record;
      setEditing(r);
      setForm({ ...EMPTY, ...r,
        mother_age_at_marriage: r.mother_age_at_marriage ?? "", mother_age_at_birth: r.mother_age_at_birth ?? "",
        children_born_alive: r.children_born_alive ?? "", birth_weight_kg: r.birth_weight_kg ?? "",
        duration_pregnancy_weeks: r.duration_pregnancy_weeks ?? "" });
      setFormError(""); setOpen(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  const payload = () => ({
    ...form,
    mother_age_at_marriage: numOrNull(form.mother_age_at_marriage),
    mother_age_at_birth: numOrNull(form.mother_age_at_birth),
    children_born_alive: numOrNull(form.children_born_alive),
    birth_weight_kg: numOrNull(form.birth_weight_kg),
    duration_pregnancy_weeks: numOrNull(form.duration_pregnancy_weeks),
    attention_at_delivery: form.attention_at_delivery || null,
    method_of_delivery: form.method_of_delivery || null,
  });

  const save = async (e) => {
    e.preventDefault();
    if (!form.date_of_birth || !form.sex) { setFormError("Date of birth and sex are required (Form No. 1, cols 1–2)."); return; }
    setBusy(true);
    try {
      const response = editing
        ? await api.put(`/hospital/records/births/${editing.birth_id}`, payload())
        : await api.post("/hospital/records/births", payload());
      const record = response.data.record;
      toast.success(editing ? "Birth record updated." : "Birth record created.");
      setOpen(false);
      setPrintRecord(record);
      load();

      // If there's a charge and payment data entered, collect the payment
      const chargeAmount = Number(form.birth_charge || 0);
      const hasPaymentData = chargeAmount > 0 &&
        (form.birth_pay_split
          ? (form.birth_pay_rows || []).some((r) => Number(r.amount) > 0)
          : Number(form.birth_charge) > 0);
      if (hasPaymentData) {
        try {
          const outstanding = chargeAmount;
          const rows = form.birth_pay_split
            ? (form.birth_pay_rows || []).map((r) => ({
                amount: round2(r.amount || 0),
                mode: r.mode,
                reference_no: r.reference_no || "",
              })).filter((r) => r.amount > 0)
            : [{ amount: round2(chargeAmount), mode: form.birth_pay_mode, reference_no: form.birth_pay_reference || "" }];
          const total = round2(rows.reduce((sum, r) => sum + r.amount, 0));
          if (total > 0 && total <= outstanding + 0.01) {
            const key = globalThis.crypto?.randomUUID?.() || `birth-pay-${Date.now()}`;
            const payPayload = form.birth_pay_split && rows.length > 1
              ? { payments: rows, idempotency_key: key }
              : { amount: rows[0].amount, mode: rows[0].mode, reference_no: rows[0].reference_no, idempotency_key: key };
            const endpoint = form.birth_pay_split && rows.length > 1
              ? `/hospital/records/births/${record.birth_id}/billing/split-payments`
              : `/hospital/records/births/${record.birth_id}/billing/payments`;
            const result = await api.post(endpoint, payPayload);
            if (result && (result.status !== undefined || result.data)) {
              const { data: detail } = await api.get(`/hospital/records/births/${record.birth_id}/billing`);
              const { data: rec } = await api.get(`/hospital/records/births/${record.birth_id}`);
              setPrintReceipt({
                invoice: detail.invoice,
                birth: rec.record,
                patient: detail.patient,
                business: detail.business,
              });
              toast.success(`₹${total} recorded against birth record ${record.record_no}.`);
            }
          }
        } catch (e) {
          toast.error(`Payment collection failed: ${apiError(e)}. Birth record was saved.`);
        }
      }
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };

  const openPrint = async (row) => {
    try {
      const { data } = await api.get(`/hospital/records/births/${row.birth_id}`);
      setPrintRecord(data.record);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const register = async (row) => {
    const regno = window.prompt("Enter the government Registration Number to mark this birth as registered:");
    if (!regno) return;
    try {
      await api.post(`/hospital/records/births/${row.birth_id}/status`,
        { status: "registered", registrar: { ...EMPTY_REGISTRAR, registration_no: regno, registration_date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) } });
      toast.success("Birth registered."); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeRow = async (row) => {
    if (!window.confirm("Delete this draft birth record?")) return;
    try { await api.delete(`/hospital/records/births/${row.birth_id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // Open the collection dialog. Defaults the amount to whatever is still
  // outstanding so "Pay full balance" is the one-click path.
  const openPay = (row) => {
    setPayError("");
    const outstanding = Number(row.outstanding || 0);
    setPayRows([{ amount: String(outstanding), mode: "cash", reference_no: "" }]);
    setSplitMode(false);
    setPayFor(row);
  };

  const addPayRow = () => {
    setPayRows([...payRows, { amount: "", mode: "cash", reference_no: "" }]);
  };

  const removePayRow = (idx) => {
    if (payRows.length <= 1) return;
    setPayRows(payRows.filter((_, i) => i !== idx));
  };

  const setPayRow = (idx, field, value) => {
    setPayRows(payRows.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  // Payment row helpers for the in-form payment section
  const addFormPayRow = () => setForm((f) => ({
    ...f, birth_pay_rows: [...(f.birth_pay_rows || []), { amount: "", mode: "cash", reference_no: "" }]
  }));
  const removeFormPayRow = (idx) => setForm((f) => ({
    ...f, birth_pay_rows: (f.birth_pay_rows || []).filter((_, i) => i !== idx)
  }));
  const setFormPayRow = (idx, field, value) => setForm((f) => ({
    ...f, birth_pay_rows: (f.birth_pay_rows || []).map((r, i) => i === idx ? { ...r, [field]: value } : r)
  }));
  const setFormPaySplit = (v) => setForm((f) => ({ ...f, birth_pay_split: v }));
  const setFormPayMode = (v) => setForm((f) => ({ ...f, birth_pay_mode: v }));
  const setFormPayReference = (v) => setForm((f) => ({ ...f, birth_pay_reference: v }));

  // Save the payment (single or split), verify the backend accepted it, then open
  // the printable receipt. Never presents a receipt when the save failed.
  const submitPay = async (event) => {
    event.preventDefault();
    if (payBusy) return;
    setPayError("");
    const outstanding = Number(payFor?.outstanding || 0);
    const rows = payRows.map((r) => ({
      amount: round2(r.amount || 0),
      mode: r.mode,
      reference_no: r.reference_no || "",
    }));
    if (rows.length < 1 || rows.every((r) => !r.amount || r.amount <= 0)) {
      setPayError("Enter at least one payment amount greater than zero.");
      return;
    }
    if (rows.some((r) => !Number.isFinite(r.amount) || r.amount <= 0)) {
      setPayError("Every payment amount must be greater than zero.");
      return;
    }
    const total = round2(rows.reduce((sum, r) => sum + r.amount, 0));
    if (total > outstanding + 0.01) {
      setPayError(`Total ₹${total} exceeds the outstanding balance of ₹${outstanding}.`);
      return;
    }
    setPayBusy(true);
    try {
      const key = globalThis.crypto?.randomUUID?.() || `birth-pay-${Date.now()}`;
      const payload = splitMode && rows.length > 1
        ? { payments: rows, idempotency_key: key }
        : { amount: rows[0].amount, mode: rows[0].mode, reference_no: rows[0].reference_no, idempotency_key: key };
      const endpoint = splitMode && rows.length > 1
        ? `/hospital/records/births/${payFor.birth_id}/billing/split-payments`
        : `/hospital/records/births/${payFor.birth_id}/billing/payments`;
      const result = await api.post(endpoint, payload);
      // Verify the save succeeded before presenting any receipt.
      if (!result || result.status === undefined && !result.data) {
        throw new Error("Payment save could not be confirmed.");
      }
      // Refresh the record + invoice so the receipt reflects the saved state.
      const { data: detail } = await api.get(`/hospital/records/births/${payFor.birth_id}/billing`);
      const { data: rec } = await api.get(`/hospital/records/births/${payFor.birth_id}`);
      setPrintReceipt({
        invoice: detail.invoice,
        birth: rec.record,
        patient: detail.patient,
        business: detail.business,
      });
      setPayFor(null);
      setPayRows([{ amount: "", mode: "cash", reference_no: "" }]);
      setSplitMode(false);
      load();
      toast.success(`₹${total} recorded against birth record ${payFor.record_no}.`);
    } catch (e) {
      setPayError(apiError(e));
      toast.error(apiError(e));
    } finally {
      setPayBusy(false);
    }
  };

  if (!can("hospital.birth.view")) return <NoAccess what="Birth records" />;

  return (
    <div data-testid="birth-records-page">
      <PageHeader overline="Vital Records" title="Birth Records — Form No. 1"
        subtitle="Government of India Birth Report (Registration of Births & Deaths Act). Capture the complete Legal (1–9) and Statistical (10–22) information."
        actions={manage && <button onClick={openNew} className={primaryBtnCls} data-testid="add-birth-btn"><Plus className="h-4 w-4" /> New birth report</button>} />

      <div className="flex flex-wrap gap-3 mb-6">
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, UHID, registration no. or date…"
          data-testid="birth-search" className={`${inputCls} max-w-sm`} />
      </div>

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="birth-table" columns={["Record No.", "Child", "Sex", "Date of Birth", "Mother", "Registration", "Status", manage ? "Actions" : ""]}
          rows={items} empty={<EmptyState title="No birth reports yet" subtitle="Create your first Form No. 1 birth report." testid="birth-empty" />}
          renderRow={(r) => (
            <tr key={r.birth_id} data-testid={`birth-row-${r.birth_id}`}>
              <Td className="font-medium text-slate-900">{r.record_no}</Td>
              <Td className="text-slate-800">{r.child_name}</Td>
              <Td className="text-slate-600">{r.sex}</Td>
              <Td className="text-slate-600">{r.date_of_birth}</Td>
              <Td className="text-slate-600">{r.mother_name || "—"}</Td>
              <Td className="text-slate-600">{r.registration_no || "—"}</Td>
              <Td><StatusPill status={r.status} /></Td>
              {manage && <Td>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => openEdit(r)} className={ghostBtnCls} data-testid={`edit-birth-${r.birth_id}`}>{r.status === "registered" ? "View" : "Edit"}</button>
                  <button onClick={() => openPrint(r)} className={ghostBtnCls}
                    data-testid={`print-birth-${r.birth_id}`}>
                    <Printer className="h-3.5 w-3.5" /> Print
                  </button>
                  {r.status !== "registered" && <button onClick={() => register(r)} className={ghostBtnCls} data-testid={`register-birth-${r.birth_id}`}>Register</button>}
                  {r.status !== "registered" && <button onClick={() => removeRow(r)} className={ghostBtnCls} data-testid={`delete-birth-${r.birth_id}`}>Delete</button>}
                  {canPay && Number(r.birth_charge) > 0 && Number(r.outstanding) > 0 && (
                    <button onClick={() => openPay(r)} className={ghostBtnCls}
                      data-testid={`collect-birth-${r.birth_id}`}>Collect ₹{r.outstanding}</button>
                  )}
                </div>
              </Td>}
            </tr>
          )} />
      )}

      <Modal open={open} onClose={() => setOpen(false)} wide
        title={editing ? `Birth report ${editing.record_no}` : "New birth report (Form No. 1)"}
        subtitle={editing?.status === "registered" ? "Registered — read only" : "Government of India Form No. 1"}
        testid="birth-modal">
        <form onSubmit={save} className="space-y-5">
          <p className="overline text-[#EA580C]">Legal Information</p>
          <Sec n={1} title="Child & Birth">
            <Grid cols={3}>
              <Txt label="Date of Birth" type="date" value={form.date_of_birth} onChange={set("date_of_birth")} testid="birth-dob" />
              <Sel label="Sex" value={form.sex} onChange={set("sex")} options={SEX_OPTS} testid="birth-sex" />
              <Sel label="Birth order (multiple births)" value={form.birth_order} onChange={set("birth_order")}
                options={[{ v: "single", l: "Single" }, { v: "twin", l: "Twin" }, { v: "triplet", l: "Triplet" }, { v: "other", l: "Other" }]} testid="birth-order" placeholder="Single" />
              <Txt label="Child's name (if any)" value={form.child.name} onChange={set("child.name")} testid="birth-child-name" />
              <Txt label="Child's Aadhaar No. (if available)" value={form.child.aadhaar_no} onChange={set("child.aadhaar_no")} testid="birth-child-aadhaar" />
            </Grid>
          </Sec>
          <Sec n={4} title="Father's Details">
            <Grid cols={2}>
              <Txt label="Name" value={form.father.name} onChange={set("father.name")} testid="birth-father-name" />
              <Txt label="Aadhaar No. (if available)" value={form.father.aadhaar_no} onChange={set("father.aadhaar_no")} testid="birth-father-aadhaar" />
              <Txt label="Mobile No." value={form.father.mobile_no} onChange={set("father.mobile_no")} testid="birth-father-mobile" />
              <Txt label="Email Id" value={form.father.email_id} onChange={set("father.email_id")} testid="birth-father-email" />
            </Grid>
          </Sec>
          <Sec n={5} title="Mother's Details">
            <Grid cols={2}>
              <Txt label="Name" value={form.mother.name} onChange={set("mother.name")} testid="birth-mother-name" />
              <Txt label="Aadhaar No. (if available)" value={form.mother.aadhaar_no} onChange={set("mother.aadhaar_no")} testid="birth-mother-aadhaar" />
              <Txt label="Mobile No." value={form.mother.mobile_no} onChange={set("mother.mobile_no")} testid="birth-mother-mobile" />
              <Txt label="Email Id" value={form.mother.email_id} onChange={set("mother.email_id")} testid="birth-mother-email" />
            </Grid>
          </Sec>
          <Sec n={6} title="Address of parents at the time of Birth"><AddressFields value={form.address_at_birth} onChange={set("address_at_birth")} prefix="birth-addr" /></Sec>
          <Sec n={7} title="Permanent address of parents"><AddressFields value={form.permanent_address} onChange={set("permanent_address")} prefix="birth-perm" /></Sec>
          <Sec n={8} title="Place of birth">
            <Grid cols={2}>
              <Sel label="Type" value={form.place_of_birth.type} onChange={set("place_of_birth.type")} options={PLACE_OPTS} testid="birth-place-type" placeholder="Select" />
              <Txt label="Hospital / Institution name" value={form.place_of_birth.institution_name} onChange={set("place_of_birth.institution_name")} testid="birth-place-name" />
            </Grid>
            {form.place_of_birth.type !== "hospital_institution" && (
              <div className="mt-4"><AddressFields value={form.place_of_birth.address} onChange={set("place_of_birth.address")} prefix="birth-place-addr" /></div>
            )}
          </Sec>
          <Sec n={9} title="Informant's Details">
            <Grid cols={2}>
              <Txt label="Name" value={form.informant.name} onChange={set("informant.name")} testid="birth-inf-name" />
              <Txt label="Aadhaar No. (if available)" value={form.informant.aadhaar_no} onChange={set("informant.aadhaar_no")} testid="birth-inf-aadhaar" />
              <Txt label="Mobile No." value={form.informant.mobile_no} onChange={set("informant.mobile_no")} testid="birth-inf-mobile" />
              <Txt label="Email Id" value={form.informant.email_id} onChange={set("informant.email_id")} testid="birth-inf-email" />
            </Grid>
            <div className="mt-4"><AddressFields value={form.informant.address} onChange={set("informant.address")} prefix="birth-inf-addr" /></div>
            <label className="mt-4 flex items-start gap-2 text-sm text-slate-700" data-testid="birth-declaration">
              <input type="checkbox" className="mt-1" checked={!!form.declaration} onChange={(e) => set("declaration")(e.target.checked)} />
              <span>I have furnished true information to the best of my knowledge and belief (Declaration under Sec. 23, RBD Act, 1969; consent for Aadhaar authentication).</span>
            </label>
            <div className="mt-4"><Grid cols={2}>
              <Txt label="Declaration date" type="date" value={form.declaration_date} onChange={set("declaration_date")} testid="birth-decl-date" />
              <Txt label="Informant signature" value={form.informant_signature} onChange={set("informant_signature")} testid="birth-signature" />
            </Grid></div>
          </Sec>

          <p className="overline text-[#EA580C] pt-2">Statistical Information</p>
          <Sec n={10} title="Town / Village of usual residence of the mother">
            <Grid cols={3}>
              <Sel label="Type" value={form.mother_residence.type} onChange={set("mother_residence.type")} options={[{ v: "town", l: "Town" }, { v: "village", l: "Village" }]} testid="birth-res-type" />
              <Txt label="Name" value={form.mother_residence.name} onChange={set("mother_residence.name")} testid="birth-res-name" />
              <Txt label="Sub-district" value={form.mother_residence.sub_district} onChange={set("mother_residence.sub_district")} testid="birth-res-sub" />
              <Txt label="State / UT" value={form.mother_residence.state_ut} onChange={set("mother_residence.state_ut")} testid="birth-res-state" />
              <Txt label="PIN Code" value={form.mother_residence.pin_code} onChange={set("mother_residence.pin_code")} testid="birth-res-pin" />
            </Grid>
          </Sec>
          <Sec n={11} title="Religion, education & occupation">
            <Grid cols={2}>
              <Sel label="Religion of Father" value={form.religion_father} onChange={set("religion_father")} options={RELIGION_OPTS} testid="birth-rel-father" />
              <Sel label="Religion of Mother" value={form.religion_mother} onChange={set("religion_mother")} options={RELIGION_OPTS} testid="birth-rel-mother" />
              <Txt label="Father's level of education" value={form.father_education} onChange={set("father_education")} testid="birth-edu-father" />
              <Txt label="Mother's level of education" value={form.mother_education} onChange={set("mother_education")} testid="birth-edu-mother" />
              <Txt label="Father's Occupation" value={form.father_occupation} onChange={set("father_occupation")} testid="birth-occ-father" />
              <Txt label="Mother's Occupation" value={form.mother_occupation} onChange={set("mother_occupation")} testid="birth-occ-mother" />
            </Grid>
          </Sec>
          <Sec n={16} title="Delivery & pregnancy details">
            <Grid cols={3}>
              <Num label="Mother's age at marriage (yrs)" value={form.mother_age_at_marriage} onChange={set("mother_age_at_marriage")} testid="birth-age-marriage" />
              <Num label="Mother's age at this birth (yrs)" value={form.mother_age_at_birth} onChange={set("mother_age_at_birth")} testid="birth-age-birth" />
              <Num label="Children born alive so far (incl. this)" value={form.children_born_alive} onChange={set("children_born_alive")} testid="birth-children-alive" />
              <Sel label="Type of attention at delivery" value={form.attention_at_delivery} onChange={set("attention_at_delivery")} options={ATTENTION_OPTS} testid="birth-attention" />
              <Sel label="Method of Delivery" value={form.method_of_delivery} onChange={set("method_of_delivery")} options={DELIVERY_OPTS} testid="birth-method" />
              <Num label="Birth Weight (kg, if available)" step="0.01" value={form.birth_weight_kg} onChange={set("birth_weight_kg")} testid="birth-weight" />
              <Num label="Duration of pregnancy (weeks)" value={form.duration_pregnancy_weeks} onChange={set("duration_pregnancy_weeks")} testid="birth-pregnancy-weeks" />
            </Grid>
          </Sec>
          <Sec title="Registrar (to be filled by the Registrar)" desc="Enter once the government registration is received.">
            <RegistrarFields value={form.registrar} onChange={set("registrar")} prefix="birth-reg" />
          </Sec>
          {manage && <Sec title="Registration charge (optional billing)"
            desc="Separate from the official record above — leaving this at zero keeps the birth registration free.">
            <Grid cols={2}>
              <Num label="Charge amount (₹)" value={form.birth_charge} onChange={set("birth_charge")} testid="birth-charge" />
              <Txt label="Charge description" value={form.birth_charge_note} onChange={set("birth_charge_note")} testid="birth-charge-note" />
            </Grid>
            <div className="mt-4 space-y-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                <input type="checkbox" checked={form.birth_pay_split}
                  onChange={(e) => setFormPaySplit(e.target.checked)} />
                <label>Split Payment</label>
              </div>
                {form.birth_pay_split ? (
                  <div className="space-y-2" data-testid="birth-form-split-rows">
                    {(form.birth_pay_rows || []).map((row, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center border border-slate-200 rounded p-2">
                        <div className="col-span-4">
                          <Num label="Amount (₹)" value={row.amount}
                            onChange={(v) => setFormPayRow(idx, "amount", v)}
                            testid={`birth-form-split-amount-${idx}`} />
                        </div>
                        <div className="col-span-4">
                          <Field label="Mode">
                            <select className={inputCls} value={row.mode}
                              onChange={(e) => setFormPayRow(idx, "mode", e.target.value)}
                              testid={`birth-form-split-mode-${idx}`}>
                              {PAY_MODES.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                            </select>
                          </Field>
                        </div>
                        <div className="col-span-3">
                          <Txt label="Reference" value={row.reference_no}
                            onChange={(v) => setFormPayRow(idx, "reference_no", v)}
                            testid={`birth-form-split-ref-${idx}`} />
                        </div>
                        <div className="col-span-1">
                          <button type="button" onClick={() => removeFormPayRow(idx)}
                            className={ghostBtnCls} data-testid={`birth-form-split-remove-${idx}`}>×</button>
                        </div>
                      </div>
                    ))}
                    <div className="col-span-12">
                      <button type="button" onClick={addFormPayRow} className={ghostBtnCls}
                        data-testid="birth-form-split-add">+ Add payment method</button>
                    </div>
                    <div className="col-span-12 text-xs text-slate-500" data-testid="birth-form-split-total">
                      Split total: <strong>₹{round2((form.birth_pay_rows || []).reduce((s, r) => s + (Number(r.amount) || 0), 0))}</strong>
                    </div>
                  </div>
                ) : (
                  <Grid cols={2}>
                    <Num label="Amount collected (₹)" value={form.birth_charge} onChange={set("birth_charge")} testid="birth-pay-amount" />
                    <Field label="Payment method">
                      <select className={inputCls} value={form.birth_pay_mode}
                        onChange={(e) => setFormPayMode(e.target.value)} testid="birth-pay-mode">
                        {PAY_MODES.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                      </select>
                    </Field>
                  </Grid>
                )}
                {!form.birth_pay_split && (
                  <Txt label="Reference no. (UPI / card / cheque)" value={form.birth_pay_reference}
                    onChange={setFormPayReference} testid="birth-pay-ref" />
                )}
            </div>
          </Sec>}

          {formError && <p className="text-sm text-red-600" data-testid="birth-form-error">{formError}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Close</button>
            {editing?.status !== "registered" && <button disabled={busy} className={primaryBtnCls} data-testid="save-birth-btn">{busy ? "Saving…" : "Save"}</button>}
          </div>
        </form>
      </Modal>
      <PrintPreview open={Boolean(printRecord)} onClose={() => setPrintRecord(null)}
        title={`Birth record ${printRecord?.record_no || ""}`} paperSize="A4"
        subtitle="Saved record — printing is optional" testid="birth-print-preview">
        <BirthRecordDoc record={printRecord} />
      </PrintPreview>
      <Modal open={Boolean(payFor)} onClose={() => setPayFor(null)}
        title={`Payable — ${payFor?.record_no || ""}`}
        subtitle="Bill amount, amount collected and payment-method breakdown. Save & Print issues the receipt."
        testid="birth-pay-modal">
        <form onSubmit={submitPay} className="space-y-4">
          <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
            Charge <strong>₹{payFor?.birth_charge}</strong> · Paid <strong>₹{payFor?.paid}</strong> ·
            Outstanding <strong data-testid="birth-pay-outstanding">₹{payFor?.outstanding}</strong>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
            <input type="checkbox" checked={splitMode}
              onChange={(e) => setSplitMode(e.target.checked)} />
            <label>Split payment across multiple methods</label>
          </div>
          {splitMode ? (
            <div className="space-y-2" data-testid="birth-pay-split-rows">
              {payRows.map((row, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center border border-slate-200 rounded p-2">
                  <div className="col-span-4">
                    <Num label="Amount (₹)" value={row.amount}
                      onChange={(v) => setPayRow(idx, "amount", v)}
                      testid={`birth-pay-split-amount-${idx}`} />
                  </div>
                  <div className="col-span-4">
                    <Field label="Mode">
                      <select className={inputCls} value={row.mode}
                        onChange={(e) => setPayRow(idx, "mode", e.target.value)}
                        testid={`birth-pay-split-mode-${idx}`}>
                        {PAY_MODES.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div className="col-span-3">
                    <Txt label="Reference" value={row.reference_no}
                      onChange={(v) => setPayRow(idx, "reference_no", v)}
                      testid={`birth-pay-split-ref-${idx}`} />
                  </div>
                  <div className="col-span-1">
                    <button type="button" onClick={() => removePayRow(idx)}
                      className={ghostBtnCls} data-testid={`birth-pay-split-remove-${idx}`}>×</button>
                  </div>
                </div>
              ))}
              <div className="col-span-12">
                <button type="button" onClick={addPayRow} className={ghostBtnCls}
                  data-testid="birth-pay-split-add">+ Add payment method</button>
              </div>
              <div className="col-span-12 text-xs text-slate-500" data-testid="birth-pay-split-total">
                Split total: <strong>₹{round2(payRows.reduce((s, r) => s + (Number(r.amount) || 0), 0))}</strong>
              </div>
            </div>
          ) : (
            <Grid cols={2}>
              <Num label="Amount (₹)" value={payRows[0].amount}
                onChange={(v) => setPayRow(0, "amount", v)} testid="birth-pay-amount" />
              <Field label="Payment mode">
                <select className={inputCls} value={payRows[0].mode}
                  onChange={(e) => setPayRow(0, "mode", e.target.value)} testid="birth-pay-mode">
                  {PAY_MODES.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                </select>
              </Field>
            </Grid>
          )}
          {!splitMode && (
            <Txt label="Reference no. (UPI / card / cheque)" value={payRows[0].reference_no}
              onChange={(v) => setPayRow(0, "reference_no", v)} testid="birth-pay-ref" />
          )}
          {payError && <p className="text-sm text-red-600" data-testid="birth-pay-error">{payError}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setPayFor(null)} className={ghostBtnCls}>Cancel</button>
            <button disabled={payBusy} className={primaryBtnCls} data-testid="birth-pay-submit">
              {payBusy ? "Saving…" : "Save & Print"}
            </button>
          </div>
        </form>
      </Modal>
      <PrintPreview open={Boolean(printReceipt)} onClose={() => setPrintReceipt(null)}
        title={`Receipt — ${printReceipt?.birth?.record_no || ""}`}
        subtitle="Birth record payment receipt" paperSize="A4"
        testid="birth-receipt-preview">
        <BirthPaymentReceiptDoc receipt={printReceipt} />
      </PrintPreview>
    </div>
  );
}
